import { LinkSlashIcon } from '@heroicons/react/24/outline';
import { Button } from '@praetorian-chariot/ui';
import React, { useState } from 'react';
import { toast } from 'sonner';

import { Modal } from '@/components/Modal';
import { PlexTracConnectionModal } from '@/components/reporting/PlexTracConnectionModal';
import { Tooltip } from '@/components/Tooltip';
import {
  useCreatePlexTracClient,
  usePlexTracClient,
} from '@/hooks/usePlextrac';
import { cn } from '@/utils/classname';

interface PlexTracClientIdProps {
  customerEmail?: string;
  isPraetorianUser?: boolean;
  className?: string;
}

export const PlexTracClientId: React.FC<PlexTracClientIdProps> = ({
  customerEmail,
  isPraetorianUser = false,
  className,
}) => {
  // Use customer context for PlexTrac client ID - each customer has their own PlexTrac client
  const {
    clientId,
    clientIdStatus,
    updatePlexTracClient,
    updatePlexTracClientStatus,
  } = usePlexTracClient(customerEmail);

  const { mutateAsync: createPlexTracClient, status: createStatus } =
    useCreatePlexTracClient(customerEmail);

  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [showConnectionModal, setShowConnectionModal] = useState(false);

  const handleConnect = async () => {
    // If customerEmail is provided (customer drawer context), show the modal with options
    if (customerEmail) {
      setShowConnectionModal(true);
      return;
    }

    // Otherwise, directly create a new client (regular settings page)
    try {
      await createPlexTracClient({
        name: 'Chariot User',
        description: 'Auto-created PlexTrac client',
      });
    } catch (error) {
      // Error handling is done by the mutation hook
    }
  };

  const handleDisconnect = async () => {
    const disconnectPromise = updatePlexTracClient('')
      .then(() => {
        setShowDisconnectConfirm(false);
      })
      .catch(error => {
        setShowDisconnectConfirm(false);
        throw new Error('Failed to disconnect PlexTrac');
      });

    toast.promise(disconnectPromise, {
      loading: 'Disconnecting PlexTrac...',
      success: 'PlexTrac disconnected successfully',
      error: 'Failed to disconnect PlexTrac',
    });
  };

  const isCreating = createStatus === 'pending';
  const isUpdating = updatePlexTracClientStatus === 'pending';
  // For new customer creation (customerEmail === undefined), don't check clientIdStatus
  const disabled =
    !isPraetorianUser ||
    (customerEmail !== undefined && clientIdStatus === 'pending') ||
    isCreating ||
    isUpdating;
  const isConnected = !!clientId;

  return (
    <>
      <div className={cn('flex items-center gap-2', className)}>
        {isConnected ? (
          <>
            {/* Disconnect icon */}
            <button
              type="button"
              onClick={() => setShowDisconnectConfirm(true)}
              className="text-chariot-text-secondary hover:text-chariot-stroke-critical cursor-pointer transition-colors disabled:cursor-not-allowed disabled:opacity-50"
              aria-label="Disconnect PlexTrac"
              disabled={disabled || isUpdating}
            >
              <LinkSlashIcon className="size-5" />
            </button>

            {/* Connected button with Praetorian green styling and tooltip */}
            <Tooltip title={`PlexTrac Client ID: ${clientId}`} placement="top">
              <Button
                label="Connected"
                styleType="primary"
                size="sm"
                onClick={() => {}}
              />
            </Tooltip>
          </>
        ) : (
          <Button
            label={isCreating ? 'Connecting...' : 'Connect'}
            styleType="primary"
            size="sm"
            onClick={handleConnect}
            disabled={disabled}
          />
        )}
      </div>

      {/* Connection Modal - Only shown for customer drawer context */}
      <PlexTracConnectionModal
        isOpen={showConnectionModal}
        onClose={() => setShowConnectionModal(false)}
        customerEmail={customerEmail}
      />

      {/* Disconnect Confirmation Modal */}
      {showDisconnectConfirm && (
        <Modal
          open={showDisconnectConfirm}
          onClose={() => setShowDisconnectConfirm(false)}
          title="Disconnect PlexTrac"
          size="lg"
          footer={{
            text: isUpdating ? 'Disconnecting...' : 'Disconnect',
            onClick: handleDisconnect,
            styleType: 'primary',
            disabled: isUpdating,
            hideCancelButton: false,
            className: '!w-32',
          }}
        >
          <div className="space-y-4">
            <p className="text-chariot-text-primary">
              Are you sure you want to disconnect PlexTrac integration?
            </p>
            <p className="text-chariot-text-secondary text-sm">
              This will remove the PlexTrac Client ID ({clientId}) from this
              configuration. A new client will be created if you reconnect.
            </p>
          </div>
        </Modal>
      )}
    </>
  );
};
